import { useState, useRef } from "react";
import { IconX } from "@tabler/icons-react";

type TagsInputProps = {
  value?: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
};

function TagsInput({
  value: tags = [],
  onChange,
  placeholder = "Add tags...",
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const processInput = (value: string) => {
    const newTags = value
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0 && !tags.includes(tag));

    if (newTags.length > 0) {
      onChange([...tags, ...newTags]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim()) {
        processInput(inputValue);
      }
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      processInput(inputValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    processInput(pastedText);
  };

  const removeTag = (indexToRemove: number) => {
    onChange(tags.filter((_, index) => index !== indexToRemove));
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <div
      className="flex min-h-11 w-full cursor-text flex-wrap items-center gap-2 overflow-hidden rounded-md border border-border-primary bg-white p-2"
      onClick={focusInput}
    >
      {tags.map((tag, index) => (
        <div
          key={index}
          className="flex min-h-5 max-w-full items-center gap-1 rounded-full border border-transparent px-2.5 py-0.5 text-sm font-medium"
          style={{
            background:
              "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, color-mix(in srgb, var(--color-primary-400) 10%, transparent), color-mix(in srgb, var(--color-secondary-400) 10%, transparent)) border-box",
          }}
        >
          <span className="truncate bg-linear-to-br from-primary-400 to-secondary-400 bg-clip-text font-medium text-transparent">
            {tag}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeTag(index);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                removeTag(index);
              }
            }}
            aria-label={`Remove ${tag}`}
            title={`Remove ${tag}`}
            className="shrink-0 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1 [&_svg]:stroke-[url(#icon-gradient)]"
          >
            <IconX className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={tags.length === 0 ? placeholder : ""}
        className="min-w-30 grow bg-white px-2 text-sm text-gray-700 focus:outline-none"
      />
    </div>
  );
}

export default TagsInput;
